'use client';

import { SubscribeModal } from '@ps/features/subscribe/components/SubscribeModal';
import { SubscribeProvider } from '@ps/features/subscribe/contexts/subscribe/provider';
import type { BioContext } from '@ps/functions/bio/bioContext';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models';
import { CreatorInfo } from '@ps/types/creator';

import { resolveSubscribeExpressIntent } from '../resolveSubscribeExpressIntent';
import { subscribeSuccessRedirect } from './subscribeSuccessRedirect';

interface SubscribeRouteContentProps {
  /** Live bio context — carries the creator's store id (`creatorId`) the picker
   *  subscribes to, and the post-success return target. */
  bioContext: BioContext;
  creatorInfo: CreatorInfo;
  /** Preselect a tier (RN `defaultLevel`). */
  defaultLevel?: PopStoreExtendedSubscriptionLevel;
  /** Floor: tiers below this render disabled (RN `minLevel`) — e.g. the DM gate's
   *  `dmMinimumSubscriptionLevel`. */
  minLevel?: PopStoreExtendedSubscriptionLevel;
  /**
   * Where to return AFTER a paid-tier checkout succeeds (`redirect_to`). Absent
   * (the plain "Subscribe" CTA) → the creator's bio home. See
   * {@link subscribeSuccessRedirect}.
   */
  successRedirectUrl?: string;
  /** Shell-provided dismiss — `router.back()` for the intercept dialog, a hard
   *  leave-to-bio for the standalone page. Wired to the picker's ✕ (dialog only)
   *  and to the checkout hand-off's pre-nav close. */
  onClose: () => void;
  /** Show the in-picker ✕. The intercept dialog does; the standalone page hides
   *  it (it exits via its own back pill on the bio backdrop). Default `true`. */
  showCloseButton?: boolean;
}

/**
 * Shared tier-picker body of `/<url_code>/subscribe`, rendered by BOTH shells:
 * the `@subscribe/(.)subscribe` intercept wraps it in a `Dialog`; the standalone
 * `subscribe/page` wraps it in a centered card on the bio backdrop. Each shell
 * runs the anonymous→login gate ({@link useIsSubscribeBlocked}) and hides
 * itself entirely for a guest, so this body only ever renders for a resolving or
 * signed-in viewer — no auth guard here.
 *
 * This is the ONE path-based subscribe surface (the bio context). Every non-bio
 * caller (my-account, dashboard my-subscriptions) uses the in-place
 * `SubscribeDialog` instead — both render this same
 * `SubscribeProvider` + `SubscribeModal` view.
 */
export function SubscribeRouteContent({
  bioContext,
  creatorInfo,
  defaultLevel,
  minLevel,
  successRedirectUrl,
  onClose,
  showCloseButton = true,
}: SubscribeRouteContentProps) {
  // The post-checkout handoff (where the buyer lands, and hiding this picker
  // while they get there) belongs to the HOST — it outlives this body. See
  // `useSubscribeCheckoutHandoff`.
  return (
    <SubscribeProvider
      creatorInfo={creatorInfo}
      onClose={onClose}
      successRedirectUrl={subscribeSuccessRedirect(
        bioContext,
        successRedirectUrl,
      )}
      // Soft checkout: on a paid-tier confirm, `router.push('/checkout')`; the
      // checkout stacks ON TOP (its own `@checkout` slot) while this picker stays
      // mounted underneath — tipping parity.
      routeCheckoutNav
      // The express "Subscribe with Apple Pay" button (bio route only) resolves its
      // intent through this server action, per the selected plan + the session user.
      resolveExpressIntent={resolveSubscribeExpressIntent}
    >
      <SubscribeModal
        defaultLevel={defaultLevel}
        minLevel={minLevel}
        showCloseButton={showCloseButton}
      />
    </SubscribeProvider>
  );
}
